// src/admin/pages/AdminCampDetails.jsx
//
// Route: /dashboard/admin/camp/:campId
//
// Camp Financial Detail page for the Admin / Founder Dashboard.
// Shows billing, company cost, profit, margin, a cost breakdown,
// a profit calculation strip, the Financial Target configuration,
// high-level operations summary, camp information, and visual
// charts — all sourced directly from the backend response.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { getAdminCampDetail, updateAdminCampFinancialTarget, getAdminCampFinancialTarget } from './adminApi';
import {
  formatCurrency,
  formatPercentage,
  formatDate,
  formatDateRange,
  formatNumber,
} from './adminFormatters';
import { getProfitClass, isNegativeProfit } from './adminHelpers';

import CampFinancialBadge from './CampFinancialBadge';
import FinancialMetric from './FinancialMetric';
import LoadingState from './LoadingState';
import ErrorState from './ErrorState';

import './adminCampDetails.css';

/* ---------------------------------------------------------------------- */
/* Icons                                                                   */
/* ---------------------------------------------------------------------- */

function IconArrowLeft(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  );
}

function IconWallet(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3M21 12h-4a2 2 0 0 0 0 4h4v-4z" />
    </svg>
  );
}

function IconReceipt(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 2h16v20l-3-2-3 2-3-2-3 2-3-2-1 2z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 8h8M8 12h8" />
    </svg>
  );
}

function IconTrendingUp(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M23 6l-9.5 9.5-5-5L1 18" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 6h6v6" />
    </svg>
  );
}

function IconPercent(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}

function IconUsers(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function IconClipboard(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 2h6a1 1 0 0 1 1 1v2H8V3a1 1 0 0 1 1-1z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2" />
    </svg>
  );
}

function IconCheckCircle(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <circle cx="12" cy="12" r="10" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
    </svg>
  );
}

function IconClock(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <circle cx="12" cy="12" r="10" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
    </svg>
  );
}

function IconChart(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18h18" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 15l4-4 3 3 5-6" />
    </svg>
  );
}

function IconTarget(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" />
    </svg>
  );
}

function IconEdit(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

/* ---------------------------------------------------------------------- */
/* Helpers                                                                 */
/* ---------------------------------------------------------------------- */

function getErrorMessage(error) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    'Something went wrong while loading this camp.'
  );
}

function clampPercentage(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(100, num));
}

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function isEmpty(value) {
  return value === null || value === undefined || value === '';
}

const CHART_COLORS = {
  billing: '#11A8A4',
  cost: '#D97706',
  profit: '#10B981',
  profitNeg: '#DC2626',
  grossPayment: '#0D8F8C',
  expense: '#D97706',
  fixedExpense: '#6366F1',
  incentive: '#8B5CF6',
  completed: '#10B981',
  pending: '#D97706',
};

/* ---------------------------------------------------------------------- */
/* Custom tooltip for currency-based charts                                */
/* ---------------------------------------------------------------------- */

function CurrencyTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-[#E2E8E8] bg-white px-3 py-2 shadow-md">
      {label && <p className="mb-1 text-xs font-semibold text-[#172B2B]">{label}</p>}
      {payload.map((entry) => (
        <p key={entry.name} className="text-xs text-[#6B7C7C]">
          <span className="font-semibold" style={{ color: entry.color || entry.fill }}>
            {entry.name}:
          </span>{' '}
          {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Sub-sections                                                            */
/* ---------------------------------------------------------------------- */

function FinancialHero({ financials }) {
  const negative = isNegativeProfit(financials?.profit);

  const cards = [
    {
      label: 'Total Billing',
      value: formatCurrency(financials?.billing_amount),
      icon: IconWallet,
      accent: '#11A8A4',
    },
    {
      label: 'Company Cost',
      value: formatCurrency(financials?.company_cost),
      icon: IconReceipt,
      accent: '#D97706',
    },
    {
      label: 'Ops Margin',
      value: formatCurrency(financials?.profit),
      icon: IconTrendingUp,
      accent: negative ? '#DC2626' : '#10B981',
      emphasize: true,
    },
    {
      label: 'Margin',
      value: formatPercentage(financials?.margin_percentage),
      icon: IconPercent,
      accent: negative ? '#DC2626' : '#10B981',
      emphasize: true,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {cards.map((c) => (
        <div
          key={c.label}
          className={`rounded-xl border bg-white p-4 shadow-sm ${
            c.emphasize ? 'border-[#11A8A4]/40' : 'border-[#E2E8E8]'
          }`}
        >
          <div
            className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${c.accent}1a`, color: c.accent }}
          >
            <c.icon style={{ width: 18, height: 18 }} />
          </div>
          <p className="text-xs font-medium uppercase tracking-wide text-[#6B7C7C]">{c.label}</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-[#172B2B]">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* NEW: Financial Target Section                                          */
/* ---------------------------------------------------------------------- */

const TARGET_STATUS_LABELS = {
  WITHIN_TARGET: 'Within Target',
  EXCEEDED: 'Target Exceeded',
  NOT_CONFIGURED: 'Target Not Configured',
};

const TARGET_STATUS_STYLES = {
  WITHIN_TARGET: 'bg-[#10B981]/10 text-[#0A7A54] ring-1 ring-inset ring-[#10B981]/25',
  EXCEEDED: 'bg-[#DC2626]/10 text-[#B91C1C] ring-1 ring-inset ring-[#DC2626]/25',
  NOT_CONFIGURED: 'bg-[#F5F7F8] text-[#6B7C7C] ring-1 ring-inset ring-[#D8E1E1]',
};

function TargetStatusBadge({ status }) {
  const label = TARGET_STATUS_LABELS[status] || status || 'Unknown';
  const style = TARGET_STATUS_STYLES[status] || TARGET_STATUS_STYLES.NOT_CONFIGURED;

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ${style}`}>
      {label}
    </span>
  );
}

function TargetValueDisplay({ label, value, formatter }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wide text-[#6B7C7C]">{label}</p>
      <p className="mt-0.5 text-base font-bold tabular-nums text-[#172B2B]">
        {isEmpty(value) ? (
          <span className="text-sm font-semibold text-[#6B7C7C]">Not configured</span>
        ) : (
          formatter ? formatter(value) : value
        )}
      </p>
    </div>
  );
}

function RemainingTargetDisplay({ remainingTarget, targetStatus }) {
  if (isEmpty(remainingTarget)) {
    return (
      <div>
        <p className="text-[11px] font-medium uppercase tracking-wide text-[#6B7C7C]">Remaining Target</p>
        <p className="mt-0.5 text-sm font-semibold text-[#6B7C7C]">Not configured</p>
      </div>
    );
  }

  const value = Number(remainingTarget);
  const exceeded = Number.isFinite(value) && value < 0;

  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wide text-[#6B7C7C]">Remaining Target</p>
      <p className={`mt-0.5 text-base font-bold tabular-nums ${exceeded ? 'text-[#DC2626]' : 'text-[#172B2B]'}`}>
        {exceeded && targetStatus === 'EXCEEDED'
          ? `Exceeded by ${formatCurrency(Math.abs(value))}`
          : formatCurrency(value)}
      </p>
    </div>
  );
}

function TargetUtilizationBar({ actualCost, targetCost, utilizationPercentage, targetStatus }) {
  const hasTarget = !isEmpty(targetCost);
  const barWidth = clampPercentage(utilizationPercentage);
  const barColor = targetStatus === 'EXCEEDED' ? '#DC2626' : '#11A8A4';

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between text-xs text-[#6B7C7C]">
        <span>Cost Utilization</span>
        <span className="font-semibold text-[#172B2B]">
          {hasTarget ? `${formatCurrency(actualCost)} / ${formatCurrency(targetCost)}` : 'Not configured'}
        </span>
      </div>
      <div className="admin-margin-track">
        <div
          className="admin-margin-fill"
          style={{ width: `${hasTarget ? barWidth : 0}%`, backgroundColor: barColor }}
        />
      </div>
      <div className="mt-1 flex items-center justify-between">
        <span className={`text-xs font-semibold ${targetStatus === 'EXCEEDED' ? 'text-[#DC2626]' : 'text-[#0D8F8C]'}`}>
          {hasTarget ? formatPercentage(utilizationPercentage) : '—'}
        </span>
      </div>
    </div>
  );
}

const TARGET_TYPE_OPTIONS = [
  { value: 'PERCENTAGE', label: 'Percentage' },
  { value: 'AMOUNT', label: 'Amount' },
];

function buildInitialForm(financialTarget) {
  return {
    contractValue: isEmpty(financialTarget?.contract_value) ? '' : String(financialTarget.contract_value),
    costTargetType: financialTarget?.cost_target_type || 'PERCENTAGE',
    costTargetPercentage: isEmpty(financialTarget?.cost_target_percentage)
      ? ''
      : String(financialTarget.cost_target_percentage),
    costTargetAmount: isEmpty(financialTarget?.cost_target_amount)
      ? ''
      : String(financialTarget.cost_target_amount),
  };
}

function validateTargetForm(form) {
  const errors = {};

  if (form.contractValue === '' || form.contractValue === null || form.contractValue === undefined) {
    errors.contractValue = 'Contract value is required.';
  } else if (!Number.isFinite(Number(form.contractValue))) {
    errors.contractValue = 'Contract value must be a number.';
  } else if (Number(form.contractValue) < 0) {
    errors.contractValue = 'Contract value must be 0 or greater.';
  }

  if (form.costTargetType === 'PERCENTAGE') {
    if (form.costTargetPercentage === '' || form.costTargetPercentage === null || form.costTargetPercentage === undefined) {
      errors.costTargetPercentage = 'Target percentage is required.';
    } else if (!Number.isFinite(Number(form.costTargetPercentage))) {
      errors.costTargetPercentage = 'Target percentage must be a number.';
    } else if (Number(form.costTargetPercentage) < 0 || Number(form.costTargetPercentage) > 100) {
      errors.costTargetPercentage = 'Target percentage must be between 0 and 100.';
    }
  } else if (form.costTargetType === 'AMOUNT') {
    if (form.costTargetAmount === '' || form.costTargetAmount === null || form.costTargetAmount === undefined) {
      errors.costTargetAmount = 'Target amount is required.';
    } else if (!Number.isFinite(Number(form.costTargetAmount))) {
      errors.costTargetAmount = 'Target amount must be a number.';
    } else if (Number(form.costTargetAmount) < 0) {
      errors.costTargetAmount = 'Target amount must be 0 or greater.';
    }
  }

  return errors;
}

function FinancialTargetSection({ campId, financialTarget, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => buildInitialForm(financialTarget));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Keep the form in sync with fresh backend data whenever it changes,
  // as long as the admin isn't actively mid-edit.
  useEffect(() => {
    if (!editing) {
      setForm(buildInitialForm(financialTarget));
    }
  }, [financialTarget, editing]);

  const targetStatus = financialTarget?.target_status || 'NOT_CONFIGURED';
  const notConfigured = targetStatus === 'NOT_CONFIGURED' && isEmpty(financialTarget?.contract_value);

  const handleEditClick = () => {
    setForm(buildInitialForm(financialTarget));
    setErrors({});
    setSaveError(null);
    setEditing(true);
  };

  const handleCancel = () => {
    setForm(buildInitialForm(financialTarget));
    setErrors({});
    setSaveError(null);
    setEditing(false);
  };

  const handleFieldChange = (field) => (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const validationErrors = validateTargetForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const payload = {
      contract_value: Number(form.contractValue),
      cost_target_type: form.costTargetType,
      ...(form.costTargetType === 'PERCENTAGE'
        ? { cost_target_percentage: Number(form.costTargetPercentage) }
        : { cost_target_amount: Number(form.costTargetAmount) }),
    };

    setSaving(true);
    setSaveError(null);

    try {
      await updateAdminCampFinancialTarget(campId, payload);
      await onSaved();
      setEditing(false);
    } catch (error) {
      setSaveError(getErrorMessage(error));
      // Stay in edit mode so the admin can correct the input.
    } finally {
      setSaving(false);
    }
  };

  // Non-authoritative preview shown only while editing.
  const preview = useMemo(() => {
    const contractValue = Number(form.contractValue);
    if (!Number.isFinite(contractValue)) return null;

    if (form.costTargetType === 'PERCENTAGE') {
      const pct = Number(form.costTargetPercentage);
      if (!Number.isFinite(pct)) return null;
      return (contractValue * pct) / 100;
    }

    const amount = Number(form.costTargetAmount);
    if (!Number.isFinite(amount)) return null;
    return amount;
  }, [form]);

  return (
    <div className="rounded-xl border border-[#E2E8E8] bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#11A8A4]/10 text-[#11A8A4]">
            <IconTarget style={{ width: 18, height: 18 }} />
          </div>
          <div>
            <h2 className="text-base font-bold text-[#172B2B]">Financial Target</h2>
            <p className="mt-0.5 text-xs text-[#6B7C7C]">
              Configure the maximum company cost allowed for this camp.
            </p>
          </div>
        </div>

        {!editing && (
          <button
            type="button"
            onClick={handleEditClick}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#E2E8E8] bg-white px-3 py-1.5 text-xs font-semibold text-[#0D8F8C] shadow-sm transition-colors hover:bg-[#F5F7F8]"
          >
            <IconEdit style={{ width: 14, height: 14 }} />
            {notConfigured ? 'Configure Financial Target' : 'Edit Financial Target'}
          </button>
        )}
      </div>

      {!editing ? (
        <div className="mt-4">
          {notConfigured && (
            <div className="mb-4 rounded-lg border border-dashed border-[#D8E1E1] bg-[#F8FAFA] px-4 py-3">
              <p className="text-sm text-[#6B7C7C]">
                Financial target is not configured for this camp.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <TargetValueDisplay
              label="Contract Value"
              value={financialTarget?.contract_value}
              formatter={formatCurrency}
            />
            <TargetValueDisplay
              label="Target Type"
              value={
                financialTarget?.cost_target_type
                  ? TARGET_TYPE_OPTIONS.find((o) => o.value === financialTarget.cost_target_type)?.label ||
                    financialTarget.cost_target_type
                  : null
              }
            />
            <TargetValueDisplay
              label={financialTarget?.cost_target_type === 'AMOUNT' ? 'Target Amount' : 'Target Percentage'}
              value={
                financialTarget?.cost_target_type === 'AMOUNT'
                  ? financialTarget?.cost_target_amount
                  : financialTarget?.cost_target_percentage
              }
              formatter={
                financialTarget?.cost_target_type === 'AMOUNT' ? formatCurrency : formatPercentage
              }
            />

            <TargetValueDisplay
              label="Maximum Allowed Cost"
              value={financialTarget?.calculated_target_amount ?? financialTarget?.target_cost_amount}
              formatter={formatCurrency}
            />
            <TargetValueDisplay
              label="Actual Company Cost"
              value={financialTarget?.actual_cost}
              formatter={formatCurrency}
            />
            <RemainingTargetDisplay
              remainingTarget={financialTarget?.remaining_target}
              targetStatus={targetStatus}
            />
          </div>

          <div className="mt-5">
            <TargetUtilizationBar
              actualCost={financialTarget?.actual_cost}
              targetCost={financialTarget?.calculated_target_amount ?? financialTarget?.target_cost_amount}
              utilizationPercentage={financialTarget?.target_utilization_percentage}
              targetStatus={targetStatus}
            />
          </div>

          <div className="mt-4 flex items-center gap-2 border-t border-[#E2E8E8] pt-4">
            <span className="text-xs font-semibold uppercase tracking-wide text-[#6B7C7C]">Status</span>
            <TargetStatusBadge status={targetStatus} />
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#6B7C7C]">
                Contract Value
              </label>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                value={form.contractValue}
                disabled={saving}
                onChange={handleFieldChange('contractValue')}
                className="w-full rounded-lg border border-[#E2E8E8] px-3 py-2 text-sm text-[#172B2B] shadow-sm focus:border-[#11A8A4] focus:outline-none focus:ring-1 focus:ring-[#11A8A4] disabled:bg-[#F5F7F8]"
                placeholder="e.g. 50000"
              />
              {errors.contractValue && (
                <p className="mt-1 text-xs font-medium text-[#DC2626]">{errors.contractValue}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#6B7C7C]">
                Cost Target Type
              </label>
              <select
                value={form.costTargetType}
                disabled={saving}
                onChange={handleFieldChange('costTargetType')}
                className="w-full rounded-lg border border-[#E2E8E8] px-3 py-2 text-sm text-[#172B2B] shadow-sm focus:border-[#11A8A4] focus:outline-none focus:ring-1 focus:ring-[#11A8A4] disabled:bg-[#F5F7F8]"
              >
                {TARGET_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {form.costTargetType === 'PERCENTAGE' ? (
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#6B7C7C]">
                  Target Percentage
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max="100"
                  value={form.costTargetPercentage}
                  disabled={saving}
                  onChange={handleFieldChange('costTargetPercentage')}
                  className="w-full rounded-lg border border-[#E2E8E8] px-3 py-2 text-sm text-[#172B2B] shadow-sm focus:border-[#11A8A4] focus:outline-none focus:ring-1 focus:ring-[#11A8A4] disabled:bg-[#F5F7F8]"
                  placeholder="e.g. 30"
                />
                {errors.costTargetPercentage && (
                  <p className="mt-1 text-xs font-medium text-[#DC2626]">{errors.costTargetPercentage}</p>
                )}
              </div>
            ) : (
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#6B7C7C]">
                  Target Amount
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  value={form.costTargetAmount}
                  disabled={saving}
                  onChange={handleFieldChange('costTargetAmount')}
                  className="w-full rounded-lg border border-[#E2E8E8] px-3 py-2 text-sm text-[#172B2B] shadow-sm focus:border-[#11A8A4] focus:outline-none focus:ring-1 focus:ring-[#11A8A4] disabled:bg-[#F5F7F8]"
                  placeholder="e.g. 15000"
                />
                {errors.costTargetAmount && (
                  <p className="mt-1 text-xs font-medium text-[#DC2626]">{errors.costTargetAmount}</p>
                )}
              </div>
            )}
          </div>

          {preview !== null && (
            <div className="mt-4 rounded-lg bg-[#F5F7F8] px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6B7C7C]">Target Preview</p>
              <p className="mt-1 text-xs text-[#6B7C7C]">
                Estimated Target Cost:{' '}
                <span className="font-semibold text-[#172B2B]">{formatCurrency(preview)}</span>
              </p>
              <p className="mt-1 text-[11px] text-[#6B7C7C]">
                This is an estimate only. The saved value is calculated by the server.
              </p>
            </div>
          )}

          {saveError && (
            <div className="mt-4 rounded-lg border border-[#DC2626]/25 bg-[#DC2626]/5 px-4 py-3">
              <p className="text-sm font-semibold text-[#B91C1C]">Unable to update financial target.</p>
              <p className="mt-0.5 text-xs text-[#B91C1C]">{saveError}</p>
            </div>
          )}

          <div className="mt-5 flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#11A8A4] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0D8F8C] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="rounded-lg border border-[#E2E8E8] bg-white px-4 py-2 text-sm font-semibold text-[#6B7C7C] shadow-sm transition-colors hover:bg-[#F5F7F8] disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Charts Section                                                          */
/* ---------------------------------------------------------------------- */

function FinancialOverviewChart({ financials }) {
  const negative = isNegativeProfit(financials?.profit);

  const data = useMemo(
    () => [
      { name: 'Billing', value: num(financials?.billing_amount), fill: CHART_COLORS.billing },
      { name: 'Company Cost', value: num(financials?.company_cost), fill: CHART_COLORS.cost },
      {
        name: 'Ops Margin',
        value: num(financials?.profit),
        fill: negative ? CHART_COLORS.profitNeg : CHART_COLORS.profit,
      },
    ],
    [financials, negative]
  );

  return (
    <div className="rounded-xl border border-[#E2E8E8] bg-white p-5 shadow-sm">
      <h2 className="mb-1 text-base font-bold text-[#172B2B]">Financial Overview</h2>
      <p className="mb-3 text-xs text-[#6B7C7C]">Billing vs. company cost vs. ops margin for this camp.</p>

      <div style={{ width: '100%', height: 260 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8E8" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#6B7C7C' }} axisLine={{ stroke: '#E2E8E8' }} tickLine={false} />
            <YAxis
              tick={{ fontSize: 11, fill: '#6B7C7C' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => formatCurrency(v)}
              width={70}
            />
            <Tooltip content={<CurrencyTooltip />} cursor={{ fill: '#F5F7F8' }} />
            <Bar dataKey="value" name="Amount" radius={[6, 6, 0, 0]} maxBarSize={70}>
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function CostBreakdownChart({ financials }) {
  const rawData = useMemo(
    () => [
      { name: 'Gross Payment', value: num(financials?.gross_payment), fill: CHART_COLORS.grossPayment },
      { name: 'Expenses', value: num(financials?.expense_amount), fill: CHART_COLORS.expense },
      { name: 'Fixed Expenses', value: num(financials?.fixed_expense_amount), fill: CHART_COLORS.fixedExpense },
      { name: 'Incentive', value: num(financials?.incentive_amount), fill: CHART_COLORS.incentive },
    ],
    [financials]
  );

  const data = rawData.filter((d) => d.value > 0);
  const hasData = data.length > 0;

  return (
    <div className="rounded-xl border border-[#E2E8E8] bg-white p-5 shadow-sm">
      <h2 className="mb-1 text-base font-bold text-[#172B2B]">Cost Composition</h2>
      <p className="mb-3 text-xs text-[#6B7C7C]">Share of each cost component in total company cost.</p>

      {hasData ? (
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={90}
                paddingAngle={2}
              >
                {data.map((entry) => (
                  <Cell key={entry.name} fill={entry.fill} stroke="#fff" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip content={<CurrencyTooltip />} />
              <Legend
                verticalAlign="bottom"
                height={36}
                iconType="circle"
                wrapperStyle={{ fontSize: 12, color: '#6B7C7C' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex h-[220px] items-center justify-center rounded-lg border border-dashed border-[#D8E1E1] bg-[#F8FAFA] text-center">
          <p className="text-sm text-[#6B7C7C]">No cost components recorded for this camp.</p>
        </div>
      )}
    </div>
  );
}

function ServicesProgressChart({ summary }) {
  const completed = num(summary?.completed_services);
  const pending = num(summary?.pending_services);
  const total = completed + pending;

  const data = [
    { name: 'Completed', value: completed, fill: CHART_COLORS.completed },
    { name: 'Pending', value: pending, fill: CHART_COLORS.pending },
  ];

  return (
    <div className="rounded-xl border border-[#E2E8E8] bg-white p-5 shadow-sm">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-base font-bold text-[#172B2B]">Services Progress</h2>
        <span className="rounded-full bg-[#11A8A4]/10 px-2.5 py-0.5 text-xs font-bold text-[#0D8F8C]">
          {formatPercentage(summary?.progress_percentage)}
        </span>
      </div>
      <p className="mb-3 text-xs text-[#6B7C7C]">Completed vs. pending services out of {formatNumber(total)} total.</p>

      {total > 0 ? (
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                startAngle={90}
                endAngle={-270}
                paddingAngle={2}
              >
                {data.map((entry) => (
                  <Cell key={entry.name} fill={entry.fill} stroke="#fff" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip formatter={(value, name) => [formatNumber(value), name]} />
              <Legend verticalAlign="bottom" height={30} iconType="circle" wrapperStyle={{ fontSize: 12, color: '#6B7C7C' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex h-[180px] items-center justify-center rounded-lg border border-dashed border-[#D8E1E1] bg-[#F8FAFA] text-center">
          <p className="text-sm text-[#6B7C7C]">No service data recorded for this camp.</p>
        </div>
      )}
    </div>
  );
}

function ChartsSection({ financials, summary }) {
  return (
    <div className="rounded-xl border border-[#E2E8E8] bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#11A8A4]/10 text-[#11A8A4]">
          <IconChart style={{ width: 18, height: 18 }} />
        </div>
        <div>
          <h2 className="text-base font-bold text-[#172B2B]">Visual Analytics</h2>
          <p className="text-xs text-[#6B7C7C]">Charts derived from the camp's financial and operations data.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <FinancialOverviewChart financials={financials} />
        <CostBreakdownChart financials={financials} />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5">
        <ServicesProgressChart summary={summary} />
      </div>
    </div>
  );
}

function CostBreakdown({ financials }) {
  return (
    <div className="rounded-xl border border-[#E2E8E8] bg-white p-5 shadow-sm">
      <h2 className="mb-1 text-base font-bold text-[#172B2B]">Cost Breakdown</h2>
      <p className="mb-3 text-xs text-[#6B7C7C]">How the company cost for this camp was built up.</p>

      <div className="admin-cost-row">
        <FinancialMetric label="Gross Technician Payment" value={formatCurrency(financials?.gross_payment)} />
      </div>
      <div className="admin-cost-row">
        <FinancialMetric label="Technician Expenses" value={formatCurrency(financials?.expense_amount)} />
      </div>
      <div className="admin-cost-row">
        <FinancialMetric label="Fixed Expenses" value={formatCurrency(financials?.fixed_expense_amount)} />
      </div>
      <div className="admin-cost-row">
        <FinancialMetric label="Incentive" value={formatCurrency(financials?.incentive_amount)} />
      </div>
      <div className="admin-cost-row total">
        <FinancialMetric
          label="Total Company Cost"
          value={formatCurrency(financials?.company_cost)}
          size="lg"
        />
      </div>
    </div>
  );
}

function ProfitCalculation({ financials }) {
  const margin = clampPercentage(financials?.margin_percentage);
  const profitClass = getProfitClass(financials?.profit);

  return (
    <div className="rounded-xl border border-[#E2E8E8] bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-base font-bold text-[#172B2B]">Profit Calculation</h2>

      <div className="flex flex-wrap items-center justify-center gap-3 rounded-lg bg-[#F5F7F8] px-4 py-4 text-center sm:justify-between">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-wide text-[#6B7C7C]">Billing</p>
          <p className="text-lg font-bold text-[#172B2B]">{formatCurrency(financials?.billing_amount)}</p>
        </div>
        <span className="admin-equation-operator">-</span>
        <div>
          <p className="text-[11px] font-medium uppercase tracking-wide text-[#6B7C7C]">Company Cost</p>
          <p className="text-lg font-bold text-[#172B2B]">{formatCurrency(financials?.company_cost)}</p>
        </div>
        <span className="admin-equation-operator">=</span>
        <div>
          <p className="text-[11px] font-medium uppercase tracking-wide text-[#6B7C7C]">Ops Margin</p>
          <p className={`text-lg font-bold ${profitClass}`}>{formatCurrency(financials?.profit)}</p>
        </div>
      </div>

      <div className="mt-5">
        <div className="mb-1.5 flex items-center justify-between text-xs text-[#6B7C7C]">
          <span>Profit Margin</span>
          <span className={`font-semibold ${profitClass}`}>
            {formatPercentage(financials?.margin_percentage)}
          </span>
        </div>
        <div className="admin-margin-track">
          <div className="admin-margin-fill" style={{ width: `${margin}%` }} />
        </div>
      </div>
    </div>
  );
}

function OperationsSummary({ summary }) {
  const cards = [
    { label: 'Patients', value: formatNumber(summary?.total_patients), icon: IconUsers, accent: '#11A8A4' },
    { label: 'Services', value: formatNumber(summary?.total_services), icon: IconClipboard, accent: '#6366F1' },
    { label: 'Completed', value: formatNumber(summary?.completed_services), icon: IconCheckCircle, accent: '#10B981' },
    { label: 'Pending', value: formatNumber(summary?.pending_services), icon: IconClock, accent: '#D97706' },
    { label: 'Progress', value: formatPercentage(summary?.progress_percentage), icon: IconTrendingUp, accent: '#11A8A4' },
  ];

  return (
    <div className="rounded-xl border border-[#E2E8E8] bg-white p-5 shadow-sm">
      <h2 className="mb-1 text-base font-bold text-[#172B2B]">Operations Summary</h2>
      <p className="mb-4 text-xs text-[#6B7C7C]">High-level execution status for this camp.</p>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg bg-[#F5F7F8] p-3">
            <div
              className="mb-2 flex h-7 w-7 items-center justify-center rounded-md"
              style={{ backgroundColor: `${c.accent}1a`, color: c.accent }}
            >
              <c.icon style={{ width: 14, height: 14 }} />
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[#6B7C7C]">{c.label}</p>
            <p className="mt-0.5 text-base font-bold tabular-nums text-[#172B2B]">{c.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-[#E2E8E8] py-2.5 last:border-b-0">
      <span className="text-sm text-[#6B7C7C]">{label}</span>
      <span className="max-w-[60%] truncate text-right text-sm font-semibold text-[#172B2B]">
        {value ?? 'Not available'}
      </span>
    </div>
  );
}

function IconUserCheck(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...props}
    >
      <circle cx="9" cy="7" r="4" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 21v-2a6 6 0 0 1 12 0v2"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16 11l2 2 4-4"
      />
    </svg>
  );
}

function TechniciansSection({ technicians }) {
  const technicianList = Array.isArray(technicians)
    ? technicians
    : [];

  return (
    <div className="rounded-xl border border-[#E2E8E8] bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-[#172B2B]">
            Technicians Working
          </h2>

          <p className="mt-1 text-xs text-[#6B7C7C]">
            Technicians assigned to this camp, their services, and financial
            details.
          </p>
        </div>

        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#11A8A4]/10 text-[#11A8A4]">
          <IconUserCheck
            style={{
              width: 18,
              height: 18,
            }}
          />
        </div>
      </div>

      {technicianList.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-[#D8E1E1] bg-[#F8FAFA] px-4 py-6 text-center">
          <p className="text-sm font-semibold text-[#172B2B]">
            No technicians assigned
          </p>

          <p className="mt-1 text-xs text-[#6B7C7C]">
            No active technician assignments were found for this camp.
          </p>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {technicianList.map((technician) => {
            const services = Array.isArray(technician?.services)
              ? technician.services
              : [];

            const financials = technician?.financials || {};

            return (
              <div
                key={technician?.id}
                className="overflow-hidden rounded-xl border border-[#E2E8E8] bg-[#F8FAFA]"
              >
                {/* -------------------------------------------------- */}
                {/* Technician Header                                  */}
                {/* -------------------------------------------------- */}

                <div className="border-b border-[#E2E8E8] bg-white p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#11A8A4]/10 text-sm font-bold uppercase text-[#0D8F8C]">
                        {technician?.name?.charAt(0) || 'T'}
                      </div>

                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-bold text-[#172B2B]">
                            {technician?.name || 'Unknown Technician'}
                          </p>

                          {technician?.resource_type && (
                            <span className="rounded-full bg-[#EEF6F6] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#0D8F8C]">
                              {technician.resource_type}
                            </span>
                          )}
                        </div>

                        <p className="mt-0.5 text-xs text-[#6B7C7C]">
                          Technician ID: {technician?.id ?? 'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* Services */}
                    <div className="flex flex-wrap gap-2">
                      {services.length > 0 ? (
                        services.map((service) => (
                          <span
                            key={service?.id}
                            className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-[#0D8F8C] ring-1 ring-inset ring-[#11A8A4]/25"
                          >
                            {service?.name || 'Unknown Service'}
                          </span>
                        ))
                      ) : (
                        <span className="rounded-full bg-[#F5F7F8] px-2.5 py-1 text-xs text-[#6B7C7C]">
                          No service assigned
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* -------------------------------------------------- */}
                {/* Financial Summary                                  */}
                {/* -------------------------------------------------- */}

                <div className="p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-wide text-[#172B2B]">
                        Technician Financials
                      </h3>

                      <p className="mt-0.5 text-[11px] text-[#6B7C7C]">
                        Cost and payment breakdown for this technician.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                    <TechnicianFinancialCard
                      label="Gross Payment"
                      value={formatCurrency(financials?.gross_payment)}
                    />

                    <TechnicianFinancialCard
                      label="Technician Expense"
                      value={formatCurrency(financials?.technician_expense)}
                    />

                    <TechnicianFinancialCard
                      label="Fixed Expense"
                      value={formatCurrency(financials?.fixed_expense)}
                    />

                    <TechnicianFinancialCard
                      label="Incentive"
                      value={formatCurrency(financials?.incentive)}
                    />

                    <TechnicianFinancialCard
                      label="Total Company Cost"
                      value={formatCurrency(financials?.total_company_cost)}
                      emphasize
                    />

                    <TechnicianFinancialCard
                      label="Advance"
                      value={formatCurrency(financials?.advance_amount)}
                    />
                  </div>

                  {/* ------------------------------------------------ */}
                  {/* Net Payable                                      */}
                  {/* ------------------------------------------------ */}

                  <div className="mt-4 flex flex-col gap-2 rounded-lg border border-[#11A8A4]/20 bg-[#11A8A4]/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#6B7C7C]">
                        Net Payable
                      </p>

                      <p className="mt-0.5 text-[11px] text-[#6B7C7C]">
                        Total payable after advance adjustment.
                      </p>
                    </div>

                    <p className="text-xl font-bold tabular-nums text-[#0D8F8C]">
                      {formatCurrency(financials?.net_payable)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TechnicianFinancialCard({
  label,
  value,
  emphasize = false,
}) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        emphasize
          ? 'border-[#D97706]/25 bg-[#FFF9F0]'
          : 'border-[#E2E8E8] bg-white'
      }`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[#6B7C7C]">
        {label}
      </p>

      <p
        className={`mt-1 text-sm font-bold tabular-nums ${
          emphasize ? 'text-[#B45309]' : 'text-[#172B2B]'
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function CampInformation({ camp }) {
  return (
    <div className="rounded-xl border border-[#E2E8E8] bg-white p-5 shadow-sm">
      <h2 className="mb-1 text-base font-bold text-[#172B2B]">Camp Information</h2>
      <p className="mb-2 text-xs text-[#6B7C7C]">Configuration and location details for this camp.</p>

      <div className="mt-2">
        <InfoRow label="Client" value={camp?.client?.name} />
        <InfoRow label="Location" value={camp?.location} />
        <InfoRow label="Branch Code" value={camp?.branch_code} />
        <InfoRow label="Branch Name" value={camp?.branch_name} />
        <InfoRow label="District" value={camp?.district} />
        <InfoRow label="State" value={camp?.state} />
        <InfoRow label="PIN Code" value={camp?.pin_code} />
        <InfoRow label="Billing Mode" value={camp?.billing_mode} />
        <InfoRow label="Start Date" value={camp?.start_date ? formatDate(camp.start_date) : null} />
        <InfoRow label="End Date" value={camp?.end_date ? formatDate(camp.end_date) : null} />
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Page                                                                     */
/* ---------------------------------------------------------------------- */

export default function AdminCampDetails() {
  const { campId } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState({ data: null, loading: true, error: null });

  const fetchDetail = useCallback(async () => {
    if (!campId) return;

    setState((prev) => ({
      ...prev,
      loading: prev.data === null,
      error: null,
    }));

    try {
      console.log('[AdminCampDetails] Loading camp:', campId);

      // 1. Load camp details
      const campResponse = await getAdminCampDetail(campId);

      console.log(
        '[AdminCampDetails] Camp detail response:',
        campResponse
      );

      // 2. Load financial target separately
      const financialTargetResponse =
        await getAdminCampFinancialTarget(campId);

      console.log(
        '[AdminCampDetails] Financial target response:',
        financialTargetResponse
      );

      // 3. Merge financial target into the camp detail response
      const mergedResponse = {
        ...campResponse,
        financial_target:
          financialTargetResponse?.financial_target || null,
      };

      setState({
        data: mergedResponse,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error(
        '[AdminCampDetails] Failed to load camp details:',
        error
      );

      console.error(
        '[AdminCampDetails] Backend response:',
        error?.response?.data
      );

      setState({
        data: null,
        loading: false,
        error,
      });
    }
  }, [campId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/dashboard/admin');
  };

  const camp = state.data?.camp || {};
  const summary = state.data?.summary || {};
  const financials = state.data?.financials || {};
  const financialTarget = state.data?.financial_target || {};

  const campStatus =
    camp?.status ||
    (camp?.is_completed ? 'COMPLETED' : camp?.ready_to_go ? 'READY' : 'SCHEDULED');

  return (
    <div className="admin-camp-details-page min-h-screen">
      <div className="mx-auto max-w-6xl space-y-5 px-4 py-6 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0D8F8C] transition-colors hover:text-[#0A6360]"
        >
          <IconArrowLeft className="h-4 w-4" />
          Back to Admin Dashboard
        </button>

        {state.loading && <LoadingState variant="camp-detail" />}

        {!state.loading && state.error && (
          <ErrorState
            title="Unable to load camp details."
            message={getErrorMessage(state.error)}
            onRetry={fetchDetail}
          />
        )}

        {!state.loading && !state.error && state.data && (
          <>
            {/* Header */}
            <div className="rounded-xl border border-[#E2E8E8] bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-bold tracking-tight text-[#172B2B]">
                      Camp #{camp?.id ?? campId}
                    </h1>
                    <CampFinancialBadge status={campStatus} />
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#6B7C7C]">
                    {camp?.client?.name && <span>Client: {camp.client.name}</span>}
                    {camp?.location && <span>Location: {camp.location}</span>}
                    {camp?.billing_mode && <span>Billing Mode: {camp.billing_mode}</span>}
                  </div>
                </div>

                <div className="text-sm text-[#6B7C7C] sm:text-right">
                  <p className="text-xs font-medium uppercase tracking-wide">Dates</p>
                  <p className="font-semibold text-[#172B2B]">
                    {formatDateRange(camp?.start_date, camp?.end_date)}
                  </p>
                </div>
              </div>
            </div>

            <FinancialHero financials={financials} />

            <FinancialTargetSection
              campId={campId}
              financialTarget={financialTarget}
              onSaved={fetchDetail}
            />

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <CostBreakdown financials={financials} />
              <ProfitCalculation financials={financials} />
            </div>

            <ChartsSection financials={financials} summary={summary} />

            <OperationsSummary summary={summary} />

            <TechniciansSection
              technicians={camp?.technicians}
            />

            <CampInformation camp={camp} />
          </>
        )}
      </div>
    </div>
  );
}